import React, { useContext, useEffect, useRef, useState } from 'react';
import {
	IStyleSettingsImageUploaderContext,
	StyleSettingsImageUploaderContext,
} from '../styleSettingsImageUploaderState/styleSettingsImageUploaderContext';
import { TFileId, TLanguageIDs, TMediaFileType, IMaxFileSizeMBStyleSettings } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { AxiosResponse } from 'axios';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import { useTranslation } from 'react-i18next';
import { openBrowseFile } from 'utils/openBrowseFile';
import { downloadBlob, postMediaFileForStyleSettings } from 'backend/additionalInformation';
import { NewButton } from 'components/Button/NewButton/NewButton';
import { Icon, IconName } from 'components/Icon/Icon';
import LoaderWithProps from 'components/Loader/LoaderWithProps';
import { ModalWrapper } from 'components/ModalWindows/ModalWrapper';
import { Radio } from 'components/Radio/Radio';
import Text from 'components/Text/Text';
import 'react-image-crop/dist/ReactCrop.css';

type TExtensionOfFile = 'gif' | 'image';
type TOutputMime = 'image/png' | 'image/jpeg';
type TImageOrientation = 'horizontal' | 'vertical';

interface IProps {
	filePath: Nullable<string>;
	itemKey: Nullable<string>;
	horizontalAspectRatio?: number;
	verticalAspectRatio?: number;
	maxFileSizeMB?: IMaxFileSizeMBStyleSettings;
	translationId: string;
	languageId: TLanguageIDs;
	groupId: string;
	mediaFileType: TMediaFileType;
	hasAbilityToUploadGif?: boolean;
	showGroupLoader: () => void;
	hideGroupLoader: () => void;
	setMediaSettingsFromNormalDisplay: (
		languageId: Nullable<TLanguageIDs>,
		groupId: string,
		translationId: string,
		mediaFileType: TMediaFileType,
		sourceUrl: string,
		fileId: TFileId,
	) => void;
}

export const CropperModalNew: React.FC<IProps> = ({
	filePath,
	itemKey,
	horizontalAspectRatio,
	verticalAspectRatio,
	maxFileSizeMB,
	translationId,
	languageId,
	groupId,
	mediaFileType,
	hasAbilityToUploadGif = false,
	showGroupLoader,
	hideGroupLoader,
	setMediaSettingsFromNormalDisplay,
}) => {
	const { t } = useTranslation('form');
	const {
		changeTypeOfModalWindow,
		changeTypeOfErrorModalWindow,
		setUploaderSrc,
		setCropperSrc,
		uploadImage,
		setNewImageUploaded,
		cropperSrc,
		typeOfModalWindow,
		uploaderSrc,
	} = useContext<IStyleSettingsImageUploaderContext>(StyleSettingsImageUploaderContext);
	const [imageOrientation, setImageOrientation] = useState<TImageOrientation>('horizontal');
	const [crop, setCrop] = useState<Crop | undefined>(undefined);
	const [isCropAreaChanged, setCropAreaChanged] = useState<boolean>(false);
	const [isSentOriginalImg, setSentOriginalImg] = useState<boolean>(true);
	const canvasRef = useRef<Nullable<HTMLCanvasElement>>(null);
	const imageRef = useRef<Nullable<HTMLImageElement>>(null);
	const [loadingImageErrorInModal, setLoadingImageErrorInModal] = useState<boolean>(false);
	const [isFirstOpen, setIsFirstOpen] = useState<boolean>(true);
	const [gifFile, setGifFile] = useState<Nullable<File | Blob>>(null);
	const [imageFile, setImageFile] = useState<Nullable<File | Blob>>(null);
	const [extensionOfFile, setExtensionOfFile] = useState<TExtensionOfFile>();
	const [allowFileUpload, setAllowFileUpload] = useState<boolean>(true);
	const [outputMime, setOutputMime] = useState<TOutputMime>('image/jpeg');
	const [dataToUpload, setDataToUpload] = useState<{
		status: 'success' | 'error';
		sourceUrl?: Nullable<string>;
		fileId?: Nullable<string>;
	}>();

	const aspectRatio = imageOrientation === 'horizontal' ? horizontalAspectRatio : verticalAspectRatio;

	const inferMimeFromPath = (path: string): TOutputMime => {
		if (path.endsWith('.png')) {
			return 'image/png';
		}
		return 'image/jpeg';
	};

	useEffect(() => {
		if (dataToUpload?.status === 'success' && dataToUpload.sourceUrl && dataToUpload.fileId) {
			setMediaSettingsFromNormalDisplay(
				languageId,
				groupId,
				translationId,
				mediaFileType,
				dataToUpload.sourceUrl,
				dataToUpload.fileId,
			);
			hideGroupLoader();
			setUploaderSrc(dataToUpload.sourceUrl);
		} else if (dataToUpload?.status === 'error') {
			hideGroupLoader();
			changeTypeOfErrorModalWindow('fileUploadError');
		}
		setDataToUpload(undefined);
		setAllowFileUpload(true);
	}, [dataToUpload]);

	const { refetch, response, loading } = useFetchData<AxiosResponse<Blob>, string>(
		filePath && !isFirstOpen ? downloadBlob : null,
		filePath || '',
	);

	useEffect(() => {
		if (isFirstOpen && typeOfModalWindow === 'cropper' && uploaderSrc) {
			setIsFirstOpen(false);
			refetch();
		} else if (typeOfModalWindow === 'cropper') {
			setIsFirstOpen(false);
		}
	}, [typeOfModalWindow]);

	useEffect(() => {
		if (response) {
			const urlFromBlob = URL.createObjectURL(response.data);
			setCropperSrc(urlFromBlob);
			const typeOfFile = response.config.url?.split('.').pop() === 'gif' ? 'gif' : 'image';
			setExtensionOfFile(typeOfFile);
			if (typeOfFile === 'image') {
				setImageFile(response.data);
				setOutputMime(inferMimeFromPath(filePath || ''));
			} else {
				setGifFile(response.data);
			}
		}
	}, [response]);

	const closeModal = () => {
		changeTypeOfModalWindow(null);
		setCrop(undefined);
		setCropAreaChanged(false);
		setSentOriginalImg(true);
	};

	const handleOrientationChange = (value: TImageOrientation) => {
		setImageOrientation(value);
		setCrop(undefined);
		setSentOriginalImg(true);
	};

	const handleGifChange = async (files: Nullable<FileList>) => {
		setLoadingImageErrorInModal(false);
		setCropAreaChanged(true);
		setCrop(undefined);
		const imgFile = files ? files[0] : '';
		if (!imgFile) {
			return;
		} else if (maxFileSizeMB && imgFile.size > 1000 * 1000 * maxFileSizeMB.gif) {
			changeTypeOfErrorModalWindow('gifFileSizeExceeded');
		} else if (imgFile.type === 'image/gif') {
			setCropperSrc(URL.createObjectURL(imgFile));
			setImageFile(null);
			setGifFile(imgFile);
			setExtensionOfFile('gif');
		} else {
			changeTypeOfErrorModalWindow('invalidGifFormat');
		}
	};

	const handleImageChange = (files: Nullable<FileList>) => {
		setLoadingImageErrorInModal(false);
		setCropAreaChanged(true);
		setCrop(undefined);
		const imgFile = files ? files[0] : '';
		if (!imgFile) {
			return;
		} else if (maxFileSizeMB && imgFile.size > 1000 * 1000 * maxFileSizeMB.img) {
			changeTypeOfErrorModalWindow('imgFileSizeExceeded');
		} else if (imgFile.type === 'image/jpeg' || imgFile.type === 'image/png') {
			setCropperSrc(URL.createObjectURL(imgFile));
			setImageFile(imgFile);
			setGifFile(null);
			setExtensionOfFile('image');
			setOutputMime(imgFile.type);
		} else {
			changeTypeOfErrorModalWindow('invalidImageFormat');
		}
	};

	const drawImageInCanvas = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
		if (canvasRef.current && imageRef.current) {
			canvasRef.current.width = imageRef.current.naturalWidth;
			canvasRef.current.height = imageRef.current.naturalHeight;

			const context = canvasRef.current.getContext('2d');
			context?.drawImage(e.currentTarget, 0, 0, canvasRef.current.width, canvasRef.current.height);
		}
	};

	const handleCropAreaChange = (c: PixelCrop) => {
		setCrop(c);
		isSentOriginalImg && setSentOriginalImg(false);
		if (canvasRef.current && imageRef.current) {
			setCropAreaChanged(true);
			canvasRef.current.width = c.width;
			canvasRef.current.height = c.height;
			const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
			const scaleY = imageRef.current.naturalHeight / imageRef.current.height;

			const context = canvasRef.current.getContext('2d');
			context?.drawImage(
				imageRef.current,
				c.x * scaleX,
				c.y * scaleY,
				c.width * scaleX,
				c.height * scaleY,
				0,
				0,
				c.width,
				c.height,
			);
		}
	};

	const applyGif = async () => {
		try {
			if (gifFile) {
				const formData = new FormData();
				formData.append('file', gifFile, 'image.gif');
				const {
					data: { fileId },
				} = await postMediaFileForStyleSettings(formData);
				setDataToUpload({ status: 'success', sourceUrl: URL.createObjectURL(gifFile), fileId });
			}
		} catch {
			setDataToUpload({ status: 'error' });
		}
	};

	const uploadOriginalImage = async (file: File | Blob) => {
		const sourceUrl = URL.createObjectURL(file);
		const fileId = await uploadImage(file);
		fileId ? setDataToUpload({ status: 'success', sourceUrl, fileId }) : setDataToUpload({ status: 'error' });
	};

	const handleApplyImageSize = () => {
		if (canvasRef.current?.width === 0 && canvasRef.current.height === 0 && imageFile) {
			uploadOriginalImage(imageFile);
		} else if (isSentOriginalImg && imageFile) {
			uploadOriginalImage(imageFile);
		} else {
			const quality = outputMime === 'image/jpeg' ? 0.8 : undefined;
			canvasRef.current?.toBlob(
				async (blob) => {
					if (blob) {
						const sourceUrl = URL.createObjectURL(blob);
						const fileId = (await uploadImage(blob)) || '';
						fileId
							? setDataToUpload({ status: 'success', sourceUrl, fileId })
							: setDataToUpload({ status: 'error' });
					}
				},
				outputMime,
				quality,
			);
		}
	};

	const handleApply = () => setAllowFileUpload(false);

	useEffect(() => {
		if (!allowFileUpload) {
			closeModal();
			showGroupLoader();
			if (extensionOfFile === 'image') {
				handleApplyImageSize();
			} else if (extensionOfFile === 'gif') {
				applyGif();
			}
			setLoadingImageErrorInModal(false);
			setNewImageUploaded(true);
		}
	}, [allowFileUpload]);

	const showErrorImage = () => {
		setLoadingImageErrorInModal(true);
	};

	const handleUploadGif = () => {
		openBrowseFile({ onChoose: handleGifChange, accept: '.gif' });
	};

	const handleUploadImage = () => {
		openBrowseFile({ onChoose: handleImageChange, accept: '.jpg, .jpeg, .png' });
	};

	const renderImage = () => {
		if (loading) {
			return (
				<div className="relative h-96 rounded overflow-hidden">
					<LoaderWithProps loaderSize="big" />
				</div>
			);
		}

		if (!cropperSrc) {
			return (
				<div className="h-96 rounded flex items-center justify-center bg-purple-01-new">
					<Icon name={IconName.image} size="smallHero" className="text-purple-03-new" />
				</div>
			);
		}

		if (loadingImageErrorInModal) {
			return (
				<div className="h-96 rounded flex items-center justify-center bg-purple-01-new" data-test="ImageError">
					<Text translationNameSpace="form" translationKey="imageLoadError" theme="neutralMedium" size="xs" />
				</div>
			);
		}

		return (
			<div className="flex justify-center">
				<ReactCrop
					className="max-h-96"
					crop={crop}
					aspect={aspectRatio}
					disabled={extensionOfFile === 'gif'}
					onChange={handleCropAreaChange}>
					<img
						ref={imageRef}
						src={cropperSrc}
						onClick={() => extensionOfFile === 'gif' && setCropAreaChanged(true)}
						onLoad={drawImageInCanvas}
						onError={showErrorImage}
						alt="Photo"
					/>
				</ReactCrop>
			</div>
		);
	};

	return (
		<ModalWrapper isVisible={typeOfModalWindow === 'cropper'}>
			<div className="absolute inset-0 p-4 flex items-center justify-center">
				<div
					className="w-full max-w-5xl p-8 rounded flex flex-col gap-4 bg-white-00-new shadow-500"
					data-test="CropperModal">
					<div className="flex items-center gap-4">
						<Text
							className="flex-1"
							translationNameSpace="mediaSettings"
							translationKey={itemKey}
							size="l"
							weight="bold"
						/>
						<NewButton
							variant="ghost"
							color="gray"
							size="small"
							icon={true}
							iconLeft={<Icon name={IconName.close} size="lg" />}
							ariaLabel={t('closeButton')}
							testId="closeCropperButton"
							onClick={closeModal}
						/>
					</div>
					{renderImage()}
					{!loading && (
						<div className="flex items-center gap-4">
							{hasAbilityToUploadGif && (
								<NewButton
									size="small"
									iconLeft={<Icon name={IconName.upload} size="lg" />}
									testId="chooseGifButton"
									onClick={handleUploadGif}>
									<Text
										translationNameSpace="form"
										translationKey="uploadGifButton"
										asFragment={true}
									/>
								</NewButton>
							)}
							<NewButton
								size="small"
								iconLeft={<Icon name={IconName.upload} size="lg" />}
								testId="chooseImageButton"
								onClick={handleUploadImage}>
								<Text
									translationNameSpace="form"
									translationKey="uploadImageButton"
									asFragment={true}
								/>
							</NewButton>
							<div className="flex-1 flex justify-end gap-4">
								<Radio
									checked={imageOrientation === 'horizontal'}
									name={`orientation-${translationId}`}
									testId="horizontalRatio"
									onChange={() => handleOrientationChange('horizontal')}>
									<Text translationNameSpace="form" translationKey="horizontalCropLabel" />
								</Radio>
								<Radio
									checked={imageOrientation === 'vertical'}
									name={`orientation-${translationId}`}
									testId="verticalRatio"
									onChange={() => handleOrientationChange('vertical')}>
									<Text translationNameSpace="form" translationKey="verticalCropLabel" />
								</Radio>
							</div>
						</div>
					)}
					<div className="flex justify-end gap-4">
						<NewButton variant="outline" color="gray" testId="cancelCropperButton" onClick={closeModal}>
							<Text translationNameSpace="form" translationKey="cancelButton" asFragment={true} />
						</NewButton>
						<NewButton
							disabled={!cropperSrc || !isCropAreaChanged || !allowFileUpload}
							testId="applyButton"
							onClick={handleApply}>
							<Text translationNameSpace="form" translationKey="applyButton" asFragment={true} />
						</NewButton>
					</div>
				</div>
			</div>
			<canvas className="hidden" ref={canvasRef} />
		</ModalWrapper>
	);
};
